<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <div ref="container" class="screen__stretch pretty-scrollbar">
      <BaseForm
        ref="form"
        v-bind="form"
        class="screen__form"
        @submit="onFormSubmit"
      />
      <div class="screen__center">
        <BaseText v-bind="note" />
        <br>
        <BaseButton
          v-bind="registrationButton"
          @click="goToSignUpScreen"
        />
      </div>
    </div>
  </BaseScreen>
</template>

<script>
import { mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import formMixin from '@/mixins/form';

import FieldLogin from '@/components/FieldLogin.vue';
import FieldPassword from '@/components/FieldPassword.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseText from '@/components/BaseText.vue';

const FIELDS = Object.freeze({
  LOGIN: 'username',
  PASSWORD: 'password',
});

export default {
  name: 'SignInScreen',
  mixins: [screenMixin, formMixin],
  components: {
    BaseButton,
    BaseText,
  },
  data() {
    return {
      values: {
        [FIELDS.LOGIN]: '',
        [FIELDS.PASSWORD]: '',
      },
      isLoginInvalid: false,
      isPasswordInvalid: false,
    };
  },
  computed: {
    titleText() {
      return this.$t('titles.signin');
    },
    pendingText() {
      return this.$t('pending.sign');
    },
    screenCase() {
      if (this.isErrorVisible) {
        return CONSTANTS.STEP_CONNECTION_ERROR;
      }
      if (!this.isPending) {
        if (this.isLoginInvalid) {
          return CONSTANTS.STEP_LOGIN_INVALID;
        }
        if (this.isPasswordInvalid) {
          return CONSTANTS.STEP_PASSWORD_INVALID;
        }
        return CONSTANTS.STEP_SIGN_IN;
      }
      return null;
    },
    isSubmitDisabled() {
      const isFieldEmpty = !this.values[FIELDS.LOGIN] || !this.values[FIELDS.PASSWORD];
      return Boolean(isFieldEmpty || this.isLoginInvalid || this.isPasswordInvalid);
    },
    retryAction() {
      return this.onFormSubmit;
    },
    form() {
      return {
        values: this.values,
        fields: [
          {
            is: FieldLogin,
            name: FIELDS.LOGIN,
            on: {
              blur: this.checkLoginComplete,
            },
          },
          {
            is: FieldPassword,
            name: FIELDS.PASSWORD,
            on: {
              blur: this.checkPasswordComplete,
            },
          },
        ],
        actions: [
          {
            type: 'submit',
            text: this.$t('actions.enter'),
            disabled: this.isSubmitDisabled,
            style: { width: '14em' },
          },
        ],
      };
    },
    note() {
      return {
        text: this.$t('texts.noRegistration'),
        theme: 'bright',
        isCenter: true,
      };
    },
    registrationButton() {
      return {
        text: this.$t('actions.register'),
        view: 'outlined',
        style: { width: '14em' },
      };
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badConnection'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.badConnection'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryErrorAction,
          },
        ],
      };
    },
  },
  created() {
    this.initActions([
      [CONSTANTS.ACTION_SIGN_UP, this.goToSignUpScreen],
    ]);
  },
  methods: {
    ...mapActions('personal', [
      'signin',
    ]),
    async onFormSubmit() {
      if (this.isPending) return;
      try {
        this.showPending();
        await this.signin(this.values);
        this.goToPersonalScreen();
      } catch (e) {
        const { status = 500 } = e?.response || {};
        if (status === 401) {
          const message = this.$t('texts.userNotFound');
          this.sayText(message);
          this.$refs.form?.setMessage(message);
          this.$refs.container.scrollTop = 0;
          this.hidePending();
        } else {
          this.showError();
        }
        console.error('signin error: ', e);
      }
    },
    checkLoginComplete() {
      const isValid = this.$refs.form?.checkFieldValidityByName(FIELDS.LOGIN);
      const value = this.values[FIELDS.LOGIN];
      this.isLoginInvalid = Boolean(value && !isValid);
    },
    checkPasswordComplete() {
      const isValid = this.$refs.form?.checkFieldValidityByName(FIELDS.PASSWORD);
      const value = this.values[FIELDS.PASSWORD];
      this.isPasswordInvalid = Boolean(value && !isValid);
    },
    goToPersonalScreen() {
      this.$router.push(this.$router.options.paths.personal);
    },
    goToSignUpScreen() {
      this.$router.push(this.$router.options.paths.signUp);
    },
  },
};
</script>
